/**
 * Signed-in hint shared with www.brxs.ca.
 *
 * The Supabase session lives in app.brxs.ca's localStorage (or sessionStorage
 * when "remember me" is off), which www.brxs.ca can never read. To let the www
 * header say "Open Bricks" / "Log out" we mirror only a boolean onto the
 * .brxs.ca parent domain. No token, no user id, no email: the cookie value is
 * literally "1". Any brxs.ca page can read it; nothing can authenticate with it.
 *
 * Log out from www goes to app.brxs.ca/logout?next=<www url>, which signs out
 * of Supabase here (where the session actually is), clears this hint and
 * returns to an allow-listed www origin.
 */

export const HINT_COOKIE = 'bricks_signed_in';
export const HINT_MAX_AGE = 60 * 60 * 24 * 30; // 30 days, refreshed on every app load

/** Parent-domain attribute only on brxs.ca hosts; host-only elsewhere (localhost, previews). */
export function hintDomain(hostname) {
  const h = String(hostname || '').toLowerCase();
  return h === 'brxs.ca' || h.endsWith('.brxs.ca') ? '.brxs.ca' : '';
}

/**
 * Build the Set-Cookie string for document.cookie.
 * persistent=false gives a browser-session cookie (matches remember-me off).
 */
export function hintCookieString({ signedIn, hostname, persistent = true, secure = true }) {
  const parts = [`${HINT_COOKIE}=${signedIn ? '1' : ''}`, 'Path=/', 'SameSite=Lax'];
  const domain = hintDomain(hostname);
  if (domain) parts.push(`Domain=${domain}`);
  if (secure) parts.push('Secure');
  if (!signedIn) parts.push('Max-Age=0');
  else if (persistent) parts.push(`Max-Age=${HINT_MAX_AGE}`);
  return parts.join('; ');
}

export function writeSignedInHint(signedIn, { persistent = true } = {}) {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  try {
    const { hostname, protocol } = window.location;
    const secure = protocol === 'https:';
    document.cookie = hintCookieString({ signedIn, hostname, persistent, secure });
    // Also clear any host-only copy so a stale one can't shadow the parent-domain value.
    if (!signedIn && hintDomain(hostname)) {
      document.cookie = `${HINT_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax${secure ? '; Secure' : ''}`;
    }
  } catch {
    /* cookies blocked (third-party iframe) — the hint is best-effort */
  }
}

const ALLOWED_RETURN = [
  /^https:\/\/(www\.)?brxs\.ca$/,
  /^https:\/\/bricks-site-[a-z0-9-]+-akasm\.vercel\.app$/,
  /^http:\/\/localhost(:\d+)?$/,
];

/** Only return to www (or a bricks-site preview). Anything else -> www home. */
export function safeReturnUrl(next) {
  const fallback = 'https://www.brxs.ca/';
  if (!next || typeof next !== 'string') return fallback;
  let url;
  try {
    url = new URL(next);
  } catch {
    return fallback;
  }
  if (url.username || url.password) return fallback;
  if (!ALLOWED_RETURN.some((re) => re.test(url.origin))) return fallback;
  return url.toString();
}
